'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  Splitter,
  IconButton,
  Table,
  Tooltip,
  Switch,
  Menu,
  StreamingMessagesPanel,
  type StreamingMessageRow,
  type ChatMentionsMenuItemType,
} from '@terra/ui-design-system';
import {
  CableIcon,
  Message01Icon,
  PaintBoardIcon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  createElement,
} from 'react';
import type {
  ColDef,
  ICellRendererParams,
  CellStyle,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { EmptyState } from '../EmptyState';
import {
  useWebSocketStore,
  type WebSocketConnection,
  type WebSocketMessage,
  type HighlightColor,
  getFullMessageData,
} from '../../store/websocket-store';
import { useLayoutStore } from '../../store/layoutStore';
import {
  getHighlightHex,
  getHighlightRowTint,
  HIGHLIGHT_OPTIONS,
} from '../annotations/constants';
import { useWebSocketListener } from '../../hooks/useWebSocketListener';
import { useStreamingSSE } from '../../hooks/useStreamingSSE';
import { useChatStore } from '../../store/chatStore';

const formatTime = (timestamp: Date | string | number): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getStatusBadge = (
  status: 'open' | 'closed' | 'error',
): {
  label: string;
} => {
  switch (status) {
    case 'open':
      return { label: 'Open' };
    case 'closed':
      return { label: 'Closed' };
    case 'error':
      return { label: 'Error' };
  }
};

function toStreamingMessageRow(message: WebSocketMessage): StreamingMessageRow {
  return {
    id: message.id,
    sequentialId: message.sequentialId,
    direction: message.direction,
    timestamp: message.timestamp,
    dataType: message.dataType,
    dataPreview: message.dataPreview,
    dataSize: message.dataSize,
    data: message.dataPreview,
  };
}

export function WebSocketHistoryTab() {
  useWebSocketListener();
  useStreamingSSE();
  const {
    connections,
    connectionOrder,
    messages,
    messageOrder,
    selectedConnectionId,
    selectedMessageId,
    captureEnabled,
    selectConnection,
    selectMessage,
    clearConnections,
    clearMessages,
    setCaptureEnabled,
    setConnectionHighlight,
    setMessageHighlight,
  } = useWebSocketStore();
  const { sendHttpMentionsToChat } = useChatStore();
  const connectionsGridApiRef = useRef<GridApi | null>(null);
  const [fullMessageData, setFullMessageData] = useState<string | null>(null);
  // Context menu state — separate for connections and messages
  const [connectionMenuId, setConnectionMenuId] = useState<string | null>(null);
  const [connectionMenuPos, setConnectionMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [connectionMenuOpen, setConnectionMenuOpen] = useState(false);
  const [messageMenuId, setMessageMenuId] = useState<string | null>(null);
  const [messageMenuPos, setMessageMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [messageMenuOpen, setMessageMenuOpen] = useState(false);
  // Get splitter sizes from layout store
  const webSocketSplitterSizes = useLayoutStore(
    (state) => state.webSocketSplitterSizes ?? [50, 50],
  );
  const setWebSocketSplitterSizes = useLayoutStore(
    (state) => state.setWebSocketSplitterSizes,
  );
  // Get ordered data
  const orderedConnections = useMemo(
    () => connectionOrder.map((id) => connections[id]).filter(Boolean),
    [connections, connectionOrder],
  );
  const selectedConnection = useMemo(
    () =>
      selectedConnectionId ? connections[selectedConnectionId] : undefined,
    [connections, selectedConnectionId],
  );
  const connectionMessages = useMemo(() => {
    if (!selectedConnectionId) return [];

    return messageOrder
      .map((id) => messages[id])
      .filter((m) => m && m.connectionId === selectedConnectionId);
  }, [selectedConnectionId, messages, messageOrder]);
  const streamingMessages = useMemo(
    () => connectionMessages.map(toStreamingMessageRow),
    [connectionMessages],
  );
  const selectedMessage = useMemo(
    () => (selectedMessageId ? messages[selectedMessageId] : undefined),
    [messages, selectedMessageId],
  );

  // Load full message data when message is selected
  useEffect(() => {
    if (selectedMessage) {
      getFullMessageData(selectedMessage).then(setFullMessageData);
    } else {
      setFullMessageData(null);
    }
  }, [selectedMessage]);
  // Sync store's selectedConnectionId into AG Grid selection so that
  // agent-opened sessions (set via selectConnection() without a row click)
  // are highlighted in the grid.
  useEffect(() => {
    const api = connectionsGridApiRef.current;

    if (!api || api.isDestroyed()) return;
    if (!selectedConnectionId) {
      api.deselectAll();

      return;
    }

    const node = api.getRowNode(selectedConnectionId);

    if (node && !node.isSelected()) {
      node.setSelected(true, true);
    }
  }, [selectedConnectionId]);
  // Connection columns
  const connectionColumns: ColDef<WebSocketConnection>[] = useMemo(
    () => [
      {
        field: 'sequentialId',
        headerName: '#',
        width: 50,
        sortable: true,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 80,
        cellRenderer: (params: ICellRendererParams<WebSocketConnection>) => {
          const status = params.value as 'open' | 'closed' | 'error';
          const { label } = getStatusBadge(status);

          return (
            <Badge variant="emphasis" size="sm">
              {label}
            </Badge>
          );
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 80,
        cellRenderer: (params: ICellRendererParams<WebSocketConnection>) => {
          const source = params.value as string | undefined;

          if (!source || source === 'proxy') return null;

          return (
            <Badge size="sm" variant="emphasis">
              {source}
            </Badge>
          );
        },
      },
      {
        field: 'host',
        headerName: 'Host',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'url',
        headerName: 'URL',
        flex: 2,
        minWidth: 180,
      },
      {
        field: 'messageCount',
        headerName: 'Msgs',
        width: 70,
        cellRenderer: (params: ICellRendererParams<WebSocketConnection>) => (
          <Badge size="sm" variant="outline">
            {params.value ?? 0}
          </Badge>
        ),
      },
      {
        field: 'timestamp',
        headerName: 'Time',
        width: 80,
        valueFormatter: (params) => formatTime(params.value),
      },
    ],
    [],
  );
  const getConnectionRowStyle = useCallback(
    (params: { data?: WebSocketConnection }): CellStyle | undefined => {
      if (params.data?.highlight) {
        return {
          backgroundColor: getHighlightRowTint(params.data.highlight),
        };
      }

      return undefined;
    },
    [],
  );
  const getMessageRowStyle = useCallback(
    (params: { data?: StreamingMessageRow }): CellStyle | undefined => {
      if (!params.data) {
        return undefined;
      }

      const stored = messages[params.data.id];

      if (stored?.highlight) {
        return {
          backgroundColor: getHighlightRowTint(stored.highlight),
        };
      }

      return undefined;
    },
    [messages],
  );
  // Handlers
  const handleConnectionSelect = useCallback(
    (connection: WebSocketConnection) => {
      selectConnection(connection.id);
      selectMessage(null);
    },
    [selectConnection, selectMessage],
  );
  const handleMessageSelect = useCallback(
    (message: StreamingMessageRow) => {
      selectMessage(message.id);
    },
    [selectMessage],
  );
  const resolveStreamingMessageData = useCallback(
    (message: StreamingMessageRow) => {
      const stored = messages[message.id];

      if (!stored) {
        return message.data;
      }

      return getFullMessageData(stored);
    },
    [messages],
  );
  const handleClearAll = useCallback(() => {
    clearConnections();
  }, [clearConnections]);
  const handleClearMessages = useCallback(() => {
    if (selectedConnectionId) {
      clearMessages(selectedConnectionId);
    }
  }, [clearMessages, selectedConnectionId]);
  const handleToggleCapture = useCallback(
    (checked: boolean) => {
      setCaptureEnabled(checked);
    },
    [setCaptureEnabled],
  );
  const handleConnectionsGridReady = useCallback(
    (event: GridReadyEvent) => {
      connectionsGridApiRef.current = event.api;
      if (selectedConnectionId) {
        const node = event.api.getRowNode(selectedConnectionId);

        if (node) node.setSelected(true, true);
      }
    },
    // selectedConnectionId intentionally omitted — this only runs once on mount
    // and the sync effect handles subsequent changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const handleConnectionContextMenu = useCallback(
    (event: { data?: WebSocketConnection; event?: Event | null }) => {
      if (event.data && event.event) {
        event.event.preventDefault();
        event.event.stopPropagation();
        const mouseEvent = event.event as MouseEvent;

        setConnectionMenuPos({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        setConnectionMenuId(event.data.id);
        setConnectionMenuOpen(true);
      }
    },
    [],
  );
  const handleMessageContextMenu = useCallback(
    (event: { data?: StreamingMessageRow; event?: Event | null }) => {
      if (event.data && event.event) {
        event.event.preventDefault();
        event.event.stopPropagation();
        const mouseEvent = event.event as MouseEvent;

        setMessageMenuPos({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        setMessageMenuId(event.data.id);
        setMessageMenuOpen(true);
      }
    },
    [],
  );
  const handleSendMessageToChat = useCallback(() => {
    if (!selectedMessage || !selectedConnection) return;
    const rawData = fullMessageData ?? selectedMessage.dataPreview;
    const direction =
      selectedMessage.direction === 'sent' ? 'Sent →' : 'Received ←';
    const label = `WS ${direction} ${selectedConnection.host}`;
    const rawText = [
      `WebSocket Message`,
      `Connection: ${selectedConnection.url}`,
      `Direction: ${selectedMessage.direction}`,
      `Type: ${selectedMessage.dataType}`,
      `Time: ${formatTime(selectedMessage.timestamp)}`,
      ``,
      rawData,
    ].join('\n');
    const mention: ChatMentionsMenuItemType = {
      id: `wsMessage-${selectedMessage.id}`,
      label,
      icon: createElement(Message01Icon, { size: 12 }),
      type: 'child',
      mentionEntity: {
        entityType: 'HttpRequest',
        entityId: selectedMessage.id,
        label,
        extraData: {
          httpRequest: {
            method: 'WS',
            url: selectedConnection.url,
            headers: {},
            body: rawData,
            rawText,
          },
        },
      },
    };

    sendHttpMentionsToChat({
      mentions: [mention],
    });
  }, [
    selectedMessage,
    selectedConnection,
    fullMessageData,
    sendHttpMentionsToChat,
  ]);

  // Empty state
  if (orderedConnections.length === 0) {
    return (
      <VStack gap="0" h="full" w="full" alignItems="stretch" overflow="hidden">
        {/* Toolbar */}
        <HStack
          w="full"
          p="1"
          borderBottomWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
          gap="1"
          alignItems="center"
          flexShrink={0}
        >
          <Badge variant="emphasis" size="sm">
            {captureEnabled ? 'Capturing' : 'Paused'}
          </Badge>

          <Box flex="1" />

          <Text fontSize="xs" color="fg.muted">
            Capture
          </Text>
          <Switch
            size="sm"
            checked={captureEnabled}
            onCheckedChange={(details) => handleToggleCapture(details.checked)}
          />
        </HStack>

        <EmptyState
          icon={CableIcon}
          title="No Connections"
          description="WebSocket and SSE connections will appear here when captured through the proxy or initiated by an agent"
        />
      </VStack>
    );
  }

  return (
    <VStack gap="0" h="full" w="full" alignItems="stretch" overflow="hidden">
      {/* Toolbar */}
      <HStack
        w="full"
        p="1"
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        gap="1"
        alignItems="center"
        flexShrink={0}
      >
        <Badge variant="emphasis" size="sm">
          {orderedConnections.length}
        </Badge>
        <Text fontSize="xs" color="fg.muted">
          Connections
        </Text>

        <Box flex="1" />

        <Tooltip title="Clear all connections">
          <IconButton
            size="xs"
            variant="ghost"
            onClick={handleClearAll}
            aria-label="Clear All"
          >
            <Delete02Icon size={12} />
          </IconButton>
        </Tooltip>

        <Text fontSize="xs" color="fg.muted">
          Capture
        </Text>
        <Switch
          size="sm"
          checked={captureEnabled}
          onCheckedChange={(details) => handleToggleCapture(details.checked)}
        />
      </HStack>

      {/* Connection context menu */}
      {connectionMenuId && (
        <Menu.Root
          open={connectionMenuOpen}
          onOpenChange={({ open }) => {
            setConnectionMenuOpen(open);
            if (!open) {
              setConnectionMenuId(null);
              setConnectionMenuPos(null);
            }
          }}
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-start',
            ...(connectionMenuPos && {
              getAnchorRect: () => ({
                x: connectionMenuPos.x,
                y: connectionMenuPos.y,
                width: 0,
                height: 0,
              }),
            }),
          }}
          onSelect={(details) => {
            if (details.value.startsWith('highlight-')) {
              if (details.value === 'highlight-clear') {
                setConnectionHighlight(connectionMenuId, undefined);
              } else {
                const color = details.value.replace('highlight-', '');

                setConnectionHighlight(
                  connectionMenuId,
                  color as HighlightColor,
                );
              }

              setConnectionMenuOpen(false);
            }
          }}
        >
          <Menu.Trigger asChild>
            <Box display="none" />
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content minW="140px" py="1">
              <Menu.Root
                positioning={{ strategy: 'fixed', placement: 'right-start' }}
              >
                <Menu.TriggerItem fontSize="xs" py="1" px="2">
                  <HStack gap="1.5">
                    <PaintBoardIcon size={12} />
                    <Text fontSize="xs">Highlight</Text>
                  </HStack>
                  <Menu.Indicator>›</Menu.Indicator>
                </Menu.TriggerItem>
                <Menu.Positioner>
                  <Menu.Content minW="140px" py="1">
                    {HIGHLIGHT_OPTIONS.map((opt) => (
                      <Menu.Item
                        key={opt.color}
                        value={`highlight-${opt.color}`}
                        fontSize="xs"
                        py="1"
                        px="2"
                      >
                        <HStack gap="1.5" alignItems="center">
                          <Box
                            w="12px"
                            h="12px"
                            borderRadius="sm"
                            style={{
                              backgroundColor: getHighlightHex(opt.color),
                            }}
                            flexShrink={0}
                          />
                          <Text fontSize="xs">{opt.label}</Text>
                        </HStack>
                      </Menu.Item>
                    ))}
                    <Menu.Separator />
                    <Menu.Item
                      value="highlight-clear"
                      fontSize="xs"
                      py="1"
                      px="2"
                    >
                      <HStack gap="1.5">
                        <Cancel01Icon size={12} />
                        <Text fontSize="xs">Clear Highlight</Text>
                      </HStack>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      )}

      {/* Message context menu */}
      {messageMenuId && (
        <Menu.Root
          open={messageMenuOpen}
          onOpenChange={({ open }) => {
            setMessageMenuOpen(open);
            if (!open) {
              setMessageMenuId(null);
              setMessageMenuPos(null);
            }
          }}
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-start',
            ...(messageMenuPos && {
              getAnchorRect: () => ({
                x: messageMenuPos.x,
                y: messageMenuPos.y,
                width: 0,
                height: 0,
              }),
            }),
          }}
          onSelect={(details) => {
            if (details.value.startsWith('highlight-')) {
              if (details.value === 'highlight-clear') {
                setMessageHighlight(messageMenuId, undefined);
              } else {
                const color = details.value.replace('highlight-', '');

                setMessageHighlight(messageMenuId, color as HighlightColor);
              }

              setMessageMenuOpen(false);
            }
          }}
        >
          <Menu.Trigger asChild>
            <Box display="none" />
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content minW="140px" py="1">
              <Menu.Root
                positioning={{ strategy: 'fixed', placement: 'right-start' }}
              >
                <Menu.TriggerItem fontSize="xs" py="1" px="2">
                  <HStack gap="1.5">
                    <PaintBoardIcon size={12} />
                    <Text fontSize="xs">Highlight</Text>
                  </HStack>
                  <Menu.Indicator>›</Menu.Indicator>
                </Menu.TriggerItem>
                <Menu.Positioner>
                  <Menu.Content minW="140px" py="1">
                    {HIGHLIGHT_OPTIONS.map((opt) => (
                      <Menu.Item
                        key={opt.color}
                        value={`highlight-${opt.color}`}
                        fontSize="xs"
                        py="1"
                        px="2"
                      >
                        <HStack gap="1.5" alignItems="center">
                          <Box
                            w="12px"
                            h="12px"
                            borderRadius="sm"
                            style={{
                              backgroundColor: getHighlightHex(opt.color),
                            }}
                            flexShrink={0}
                          />
                          <Text fontSize="xs">{opt.label}</Text>
                        </HStack>
                      </Menu.Item>
                    ))}
                    <Menu.Separator />
                    <Menu.Item
                      value="highlight-clear"
                      fontSize="xs"
                      py="1"
                      px="2"
                    >
                      <HStack gap="1.5">
                        <Cancel01Icon size={12} />
                        <Text fontSize="xs">Clear Highlight</Text>
                      </HStack>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      )}

      {/* Content */}
      <Box flex="1" overflow="hidden">
        <Splitter.Root
          orientation="vertical"
          h="100%"
          w="full"
          defaultSize={webSocketSplitterSizes}
          panels={[{ id: 'connections' }, { id: 'messages' }]}
          onResizeEnd={({ size }) => {
            if (setWebSocketSplitterSizes) {
              setWebSocketSplitterSizes(size as [number, number]);
            }
          }}
          borderWidth="0"
          gap={0}
        >
          {/* Connections panel */}
          <Splitter.Panel id="connections" borderWidth="0" h="full" w="full">
            <Box
              h="full"
              w="full"
              overflow="hidden"
              style={{ position: 'relative', zIndex: 1 }}
            >
              <Table
                columnDefs={connectionColumns}
                rowData={orderedConnections}
                getRowId={(params: { data: WebSocketConnection }) =>
                  params.data.id
                }
                rowSelection="single"
                onGridReady={handleConnectionsGridReady}
                onRowClicked={(event: { data?: WebSocketConnection }) => {
                  if (event.data) handleConnectionSelect(event.data);
                }}
                onCellContextMenu={handleConnectionContextMenu}
                getRowStyle={getConnectionRowStyle}
                rowHeight={32}
                headerHeight={28}
                noBorder={true}
                fontSize={11}
                headerFontSize={10}
                animateRows={false}
                defaultColDef={{
                  sortable: false,
                  filter: false,
                  resizable: true,
                }}
                suppressCellFocus={true}
                hideColumnControls={true}
              />
            </Box>
          </Splitter.Panel>

          <Splitter.ResizeTrigger
            id="connections:messages"
            height="1px"
            minHeight="1px"
            bg="border.subtle"
            cursor="row-resize"
            _hover={{ bg: 'border.default' }}
          />

          <Splitter.Panel id="messages" borderWidth="0" h="full" w="full">
            {!selectedConnectionId ? (
              <Box
                h="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text color="fg.muted" fontSize="xs">
                  Select a connection to view messages
                </Text>
              </Box>
            ) : (
              <StreamingMessagesPanel
                messages={streamingMessages}
                selectedMessageId={selectedMessageId}
                onSelectMessage={handleMessageSelect}
                resolveMessageData={resolveStreamingMessageData}
                getRowStyle={getMessageRowStyle}
                onCellContextMenu={handleMessageContextMenu}
                height="100%"
                toolbarEnd={
                  <Tooltip title="Clear messages">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={handleClearMessages}
                      aria-label="Clear Messages"
                    >
                      <Delete02Icon size={12} />
                    </IconButton>
                  </Tooltip>
                }
                detailHeaderEnd={
                  selectedMessage ? (
                    <Tooltip title="Add to Chat">
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label="Add to Chat"
                        onClick={handleSendMessageToChat}
                      >
                        <Message01Icon size={12} />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
              />
            )}
          </Splitter.Panel>
        </Splitter.Root>
      </Box>
    </VStack>
  );
}
